import { memo, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  useStore,
  type EdgeProps,
} from '@xyflow/react';
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { EdgeFloatingToolbar } from '../selection/surfaces/floating/edge-floating-toolbar';
import { deleteEdges } from './delete-edges';
import { EdgeLabel } from './edge-label';
import {
  DEFAULT_EDGE_DATA,
  dashArrayFor,
  pathFor,
  type EdgeVisualData,
} from './edge-visuals';
import styles from './nula-edge.module.css';

export const NulaEdge = memo(function NulaEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  markerEnd,
  markerStart,
  interactionWidth,
}: EdgeProps) {
  const d: EdgeVisualData = { ...DEFAULT_EDGE_DATA, ...((data ?? {}) as Partial<EdgeVisualData>) };

  // Show the floating edge toolbar only when this edge is the SOLE
  // selected element on the canvas (per design Q12: multi-edge / mixed
  // selection hides the toolbar). Computed via RF's store so we don't
  // re-render on every selection change of unrelated elements.
  const isSoleSelection = useStore((s) => {
    if (!selected) return false;
    let count = 0;
    s.edges.forEach((e) => { if (e.selected) count++; });
    s.nodes.forEach((n) => { if (n.selected) count++; });
    return count === 1;
  });
  const zoom = useStore((s) => s.transform[2]);
  const inverseZoom = zoom > 0 ? 1 / zoom : 1;
  const doc = useCanvasDoc();
  const binding = useCanvasBinding();

  // Bumped to open the inline-label input. Toolbar T-button increments
  // it; EdgeLabel watches the value and enters edit mode on change.
  const [labelEditToken, setLabelEditToken] = useState(0);

  const [edgePath, labelX, labelY] = pathFor(d.curvature, {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  }, d.stepOffset);

  const dashArray = dashArrayFor(d.strokeStyle, d.strokeWidth);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        interactionWidth={interactionWidth}
        // eslint-disable-next-line nula-style/no-inline-style -- React Flow's stylesheet sets stroke:none on edge paths; per-instance stroke data must be inline
        style={{
          // Per-instance edge appearance from user data. React Flow's default
          // stylesheet sets `stroke: none` on .react-flow__edge-path, so
          // these MUST be inline (or at higher specificity than the lib's
          // stylesheet). Inline style is the simplest correct path here.
          stroke: d.stroke,
          strokeWidth: d.strokeWidth,
          strokeLinecap: 'round',
          ...(dashArray ? { strokeDasharray: dashArray } : {}),
        }}
        data-edge-id={id}
      />

      {selected && (
        <>
          <circle
            data-edge-endpoint={id}
            cx={sourceX}
            cy={sourceY}
            r={5}
            className={styles.endpoint}
          />
          <circle
            data-edge-endpoint={id}
            cx={targetX}
            cy={targetY}
            r={5}
            className={styles.endpoint}
          />
        </>
      )}

      {doc && (
        <EdgeLabelRenderer>
          {/* Inline label pill at the path midpoint. Always rendered so
              users can read existing labels without selecting the edge.
              Position is passed via CSS custom properties consumed by
              .labelAnchor in NulaEdge.module.css. */}
          <div
            className={styles.labelAnchor}
            style={{
              '--label-x': `${labelX}px`,
              '--label-y': `${labelY}px`,
              // The chip is edged in the edge's own colour (edge-label.module.css).
              '--edge-label-accent': d.stroke,
            } as React.CSSProperties}
          >
            <EdgeLabel
              doc={doc}
              clientId={doc.clientID}
              edgeId={id}
              value={d.label}
              editToken={labelEditToken}
            />
          </div>

          {isSoleSelection && (
            <div
              className={styles.toolbarAnchor}
              style={{
                '--label-x': `${labelX}px`,
                '--label-y': `${labelY}px`,
                '--inverse-zoom': `${inverseZoom}`,
              } as React.CSSProperties}
            >
              <EdgeFloatingToolbar
                doc={doc}
                clientId={doc.clientID}
                edgeId={id}
                onDelete={(eid) => deleteEdges(doc, doc.clientID, [eid], binding)}
                onEditLabel={() => setLabelEditToken((n) => n + 1)}
              />
            </div>
          )}
        </EdgeLabelRenderer>
      )}
    </>
  );
});
